import { useCallback, useEffect, useState } from 'react';
import { Box, Fab, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PALETTE } from '@/theme/tokens';

/**
 * The create menu, as a quarter-circle of spokes.
 *
 * WHY NOT SpeedDial
 *
 * MUI's SpeedDial only stacks its actions in a straight line. That is fine for
 * the two it used to carry; at seven it becomes a column tall enough to run off
 * the top of a laptop screen, and every item sits at a different distance from
 * the button you just pressed — the last one is a 400px journey. An arc keeps
 * all seven the SAME distance from the trigger, which is the whole argument for
 * a radial menu: no item is cheaper to reach than any other, so the order can
 * follow meaning instead of frequency.
 *
 * GEOMETRY
 *
 * The trigger lives in the bottom-right corner, so the only free space is up
 * and to the left — exactly one quadrant. Spokes are laid from 90° (straight
 * up) to 180° (straight left), evenly spaced, on a single radius. Screen y
 * grows downward, hence the negated sine.
 *
 * The radius is derived from the spacing the buttons need, not picked by eye:
 * arc length between neighbours is r × Δθ, so a 48px button wanting ~60px of
 * breathing room across six gaps needs r ≈ 230. Shrink the radius on a phone
 * and the buttons shrink with it, or they would overlap.
 *
 * Nothing here knows what the actions DO. The host owns every dialog and hands
 * over an onClick — same reasoning as ConnectPanels: a menu that opened its own
 * dialogs would have to import all seven, and this component is mounted on
 * every single page of the app.
 */

const START_ANGLE = 90;   // straight up
const END_ANGLE = 180;    // straight left

export default function CreateWheel({ actions = [], centerpiece = null, ariaLabel = 'Create' }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);

  const radius = isDesktop ? 230 : 170;
  const size = isDesktop ? 48 : 40;

  /*
   * The centrepiece sits on its OWN, shorter radius, halfway between the
   * trigger and the arc, at 135° — the bisector, so it is the one thing the
   * eye lands on when the wheel opens.
   *
   * That inner ring is empty by construction: the seven spokes are all out at
   * `radius`, so nothing has to move to make room. And it is bigger, because
   * "one of these is not like the others" is the entire message. A spoke of the
   * same size in the same arc would read as an eighth way to make a post.
   */
  const centreRadius = isDesktop ? 120 : 95;
  const centreSize = isDesktop ? 64 : 54;

  const close = useCallback(() => setOpen(false), []);

  // Escape closes. A menu that covers most of the screen and can only be
  // dismissed by aiming at it is a trap for anyone who opened it by accident.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!actions.length) return null;

  const last = actions.length - 1;

  return (
    <>
      {/*
        Catches the outside click and dims the page a little, so the arc reads
        as a layer over the content rather than as seven buttons that have
        appeared IN it. Transparent to the eye at rest, never to the pointer.
      */}
      <Box
        onClick={close}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.speedDial - 1,
          bgcolor: 'rgba(15,23,42,.18)',
          backdropFilter: 'blur(1.5px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity .2s ease',
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          right: { xs: 20, md: 28 },
          bottom: { xs: 20, md: 28 },
          width: 56,
          height: 56,
          zIndex: theme.zIndex.speedDial,
        }}
      >
        {actions.map((action, i) => {
          const angle = last === 0
            ? (START_ANGLE + END_ANGLE) / 2
            : START_ANGLE + ((END_ANGLE - START_ANGLE) * i) / last;
          const rad = (angle * Math.PI) / 180;
          const dx = radius * Math.cos(rad);
          const dy = -radius * Math.sin(rad);

          const Icon = action.icon;

          return (
            <Tooltip key={action.key} title={action.label} placement="left" arrow>
              <Fab
                size="small"
                aria-label={action.label}
                tabIndex={open ? 0 : -1}
                onClick={() => { close(); action.onClick?.(); }}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: size,
                  height: size,
                  minHeight: size,
                  bgcolor: '#fff',
                  color: action.color,
                  boxShadow: '0 8px 24px -8px rgba(15,23,42,.45)',
                  /*
                   * Closed, every spoke sits exactly under the trigger at zero
                   * scale — so opening is one continuous movement outward
                   * rather than seven buttons appearing where they will be.
                   */
                  transform: open
                    ? `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(1)`
                    : 'translate(-50%, -50%) scale(0)',
                  opacity: open ? 1 : 0,
                  transition: 'transform .34s cubic-bezier(.2,.9,.3,1.2), opacity .2s ease, background .16s ease',
                  // Fan out from the trigger outward, fold back in reverse, so
                  // the motion reads as one gesture in both directions.
                  transitionDelay: `${(open ? i : last - i) * 26}ms`,
                  '&:hover': { bgcolor: action.color, color: '#fff' },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'opacity .12s linear',
                    transitionDelay: '0ms',
                    transform: open
                      ? `translate(-50%, -50%) translate(${dx}px, ${dy}px)`
                      : `translate(-50%, -50%) translate(${dx}px, ${dy}px)`,
                    visibility: open ? 'visible' : 'hidden',
                  },
                }}
              >
                <Icon sx={{ fontSize: isDesktop ? 21 : 19 }} />
              </Fab>
            </Tooltip>
          );
        })}

        {centerpiece && (() => {
          const rad = (135 * Math.PI) / 180;
          const dx = centreRadius * Math.cos(rad);
          const dy = -centreRadius * Math.sin(rad);
          const Icon = centerpiece.icon;

          return (
            <Tooltip title={centerpiece.label} placement="left" arrow>
              <Fab
                aria-label={centerpiece.label}
                tabIndex={open ? 0 : -1}
                onClick={() => { close(); centerpiece.onClick?.(); }}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: centreSize,
                  height: centreSize,
                  minHeight: centreSize,
                  color: '#fff',
                  background: centerpiece.gradient || `linear-gradient(135deg, ${PALETTE.primary}, #8B5CF6)`,
                  boxShadow: `0 14px 34px -10px ${PALETTE.primary}`,
                  transform: open
                    ? `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(1)`
                    : 'translate(-50%, -50%) scale(0)',
                  opacity: open ? 1 : 0,
                  transition: 'transform .38s cubic-bezier(.2,.9,.3,1.2), opacity .2s ease, filter .16s ease',
                  // Leads the fan-out on the way open and leaves last on the
                  // way closed, so it reads as the headline of the menu.
                  transitionDelay: open ? '0ms' : `${last * 26 + 40}ms`,
                  '&:hover': { filter: 'brightness(1.08)' },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'opacity .12s linear',
                    transitionDelay: '0ms',
                    transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`,
                    visibility: open ? 'visible' : 'hidden',
                  },
                }}
              >
                <Icon sx={{ fontSize: isDesktop ? 28 : 24 }} />
              </Fab>
            </Tooltip>
          );
        })()}

        <Fab
          color="primary"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: 56,
            height: 56,
            boxShadow: '0 10px 28px rgba(61,90,254,.42)',
            '& .wheelPlus': {
              transition: 'transform .3s cubic-bezier(.2,.9,.3,1.2)',
              transform: open ? 'rotate(135deg)' : 'none',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            },
          }}
        >
          {/* One icon that turns into the close affordance, rather than two
              that swap — a swap loses the connection between the two states. */}
          <AddIcon className="wheelPlus" />
        </Fab>
      </Box>
    </>
  );
}
